import type { MachineView, View } from './derive'
import { Chip, NetDot } from './parts'
import { ago } from './format'

const NET_TEXT = { online: 'Online', not_joined: 'Not joined yet' } as const

/** The machine list: tick to select, press to open. One row per machine, problems first. */
export function MachineRows({
  view,
  machines,
  selected,
  onSelect,
  open,
  onOpen,
  total,
}: {
  view: View
  machines: readonly MachineView[]
  selected: ReadonlySet<string>
  onSelect: (next: ReadonlySet<string>) => void
  open: string | null
  onOpen: (id: string) => void
  total: number
}): JSX.Element {
  const allShown = machines.length > 0 && machines.every((m) => selected.has(m.id))
  const toggle = (id: string): void => {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onSelect(next)
  }
  return (
    <section className="cx-panel" aria-label="Machines">
      <div className="cx-panel__head cx-panel__head--slim">
        <label className="row" style={{ gap: 10 }}>
          <input
            type="checkbox"
            checked={allShown}
            onChange={() => {
              const next = new Set(selected)
              for (const m of machines) (allShown ? next.delete(m.id) : next.add(m.id))
              onSelect(next)
            }}
          />
          <span>{allShown ? 'Clear shown' : `Select all ${machines.length} shown`}</span>
        </label>
        <span className="muted cx-small">
          {machines.length} of {total} machines
        </span>
      </div>
      <div className="cx-scroll">
        <div className="cx-table" role="table" aria-label="Machines">
          <div className="cx-table__row cx-table__row--head" role="row">
            <span role="columnheader" />
            <span role="columnheader">Machine</span>
            <span role="columnheader">Groups</span>
            <span role="columnheader">Network</span>
            <span role="columnheader">Teams</span>
            <span role="columnheader" className="cx-right">People</span>
          </div>
          <div className="cx-table__body">
            {machines.map((m) => {
              const teamIds = new Set<string>()
              let direct = 0
              for (const paths of m.access.values()) {
                for (const p of paths) {
                  if (p.teamId) teamIds.add(p.teamId)
                  else direct++
                }
              }
              const groups = m.groupIds.map((id) => view.group(id)).filter((g) => g && g.kind === 'custom')
              return (
                <div key={m.id} className={`cx-table__row ${open === m.id ? 'is-open' : ''}`} role="row">
                  <span role="cell">
                    <input type="checkbox" aria-label={`Select ${m.name}`} checked={selected.has(m.id)} onChange={() => toggle(m.id)} />
                  </span>
                  <button role="cell" className="cx-table__open" onClick={() => onOpen(m.id)} aria-label={`${m.name}: open details`}>
                    <NetDot state={m.net} />
                    <span className="cx-mono cx-strong">{m.name}</span>
                    {m.kind === 'computer' ? <span className="muted cx-small">PC</span> : null}
                  </button>
                  <span role="cell" className="cx-chips">
                    {groups.slice(0, 2).map((g) => (
                      <Chip key={g?.id} tone="plain">{g?.name}</Chip>
                    ))}
                    {groups.length > 2 ? <span className="muted cx-small">+{groups.length - 2}</span> : null}
                  </span>
                  <span role="cell" className="stack" style={{ gap: 2 }}>
                    <span className={`cx-net is-${m.net}`}>
                      {m.net === 'offline' ? `Offline${m.device?.lastSeen ? ` · ${ago(m.device.lastSeen)}` : ''}` : NET_TEXT[m.net]}
                    </span>
                    {m.device?.addresses[0] ? <span className="cx-mono muted cx-small">{m.device.addresses[0]}</span> : null}
                  </span>
                  <span role="cell" className="cx-chips cx-chips--line">
                    {[...teamIds].slice(0, 2).map((id) => (
                      <Chip key={id}>{view.team(id)?.name}</Chip>
                    ))}
                    {teamIds.size > 2 ? <Chip tone="plain">+{teamIds.size - 2}</Chip> : null}
                    {direct ? <Chip tone="plain">+{direct} {direct === 1 ? 'person' : 'people'}</Chip> : null}
                    {teamIds.size === 0 && !direct ? <span className="muted cx-small">Nobody</span> : null}
                  </span>
                  <span role="cell" className="cx-right cx-mono cx-strong">{m.access.size}</span>
                </div>
              )
            })}
            {machines.length === 0 ? <p className="muted cx-empty">Nothing matches.</p> : null}
          </div>
        </div>
      </div>
    </section>
  )
}
